import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Paper,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppealDetail } from '../../hooks/useAppeal';
import type { AppealStatus, SanctionType, ReviewResult } from '../../types/report';
import { formatDateTimeKST } from '../../utils/dateUtils';

const STATUS_CONFIG: Record<AppealStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
  SUBMITTED: { label: '접수됨', color: 'default' },
  UNDER_REVIEW: { label: '심사 중', color: 'primary' },
  ACCEPTED: { label: '이의 인정', color: 'success' },
  REJECTED: { label: '이의 기각', color: 'error' },
  CLOSED: { label: '종결', color: 'default' },
};

const SANCTION_LABELS: Record<SanctionType, string> = {
  WARNING: '경고',
  SUSPENSION: '7일 이용 정지',
  BAN: '영구 차단',
};

const AppealDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const appealId = id ? parseInt(id, 10) : null;

  const { appeal, loading, error } = useAppealDetail(appealId);


  const canReappeal =
    appeal?.status === 'REJECTED' || appeal?.status === 'CLOSED';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>이의제기 상세</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {appeal && (
          <>
            {/* 상태 + 제재 정보 */}
            <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={SANCTION_LABELS[appeal.sanctionType]}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={STATUS_CONFIG[appeal.status].label}
                  color={STATUS_CONFIG[appeal.status].color}
                  size="small"
                />
              </Box>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                접수일: {formatDateTimeKST(appeal.createdAt)}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                이의제기 기한: {formatDateTimeKST(appeal.deadline)}
              </Typography>
            </Paper>

            {/* 이의 사유 */}
            <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 1.5 }}>내 이의 사유</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Typography sx={{ fontSize: '1rem', lineHeight: 1.8, whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {appeal.reason}
              </Typography>
            </Paper>

            {/* 심사 결과 */}
            {(appeal.reviewResult || appeal.reviewedAt) && (
              <Paper
                elevation={0}
                sx={{
                  p: 3, mb: 2, borderRadius: 2,
                  border: '1px solid',
                  borderColor: appeal.reviewResult === 'ACCEPTED' ? 'success.light' : 'error.light',
                  bgcolor: appeal.reviewResult === 'ACCEPTED' ? 'rgba(76,175,80,0.05)' : 'rgba(244,67,54,0.05)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: appeal.reviewResult === 'ACCEPTED' ? 'success.main' : 'error.main' }}>
                    심사 결과: {appeal.reviewResult === 'ACCEPTED' ? '이의 인정' : '이의 기각'}
                  </Typography>
                  {appeal.reviewedAt && (
                    <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                      {formatDateTimeKST(appeal.reviewedAt)}
                    </Typography>
                  )}
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                {appeal.reviewResult === 'ACCEPTED' && appeal.compensation && (
                  <Alert severity="success" sx={{ mb: 1.5, fontSize: '0.95rem' }}>
                    <strong>보상 내용:</strong> {appeal.compensation}
                  </Alert>
                )}

                {appeal.reviewResult === 'REJECTED' && (
                  <>
                    <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.9rem' }}>
                      동일 제재 건에 대한 재이의는 <strong>1회</strong>만 가능합니다.
                    </Alert>
                    {canReappeal && (
                      <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        color="error"
                        onClick={() => navigate(`/appeal?sanctionId=${appeal.sanctionId}&sanctionType=${appeal.sanctionType}`)}
                        sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700 }}
                      >
                        재이의하기
                      </Button>
                    )}
                  </>
                )}
              </Paper>
            )}

            {/* 심사 중 안내 */}
            {appeal.status === 'UNDER_REVIEW' && (
              <Alert severity="info" sx={{ fontSize: '0.9rem' }}>
                현재 심사가 진행 중입니다. 접수일로부터 7영업일 이내에 결과를 안내드립니다.
              </Alert>
            )}

            {/* 접수 완료 안내 */}
            {appeal.status === 'SUBMITTED' && (
              <Alert severity="info" sx={{ fontSize: '0.9rem' }}>
                이의제기가 접수되었습니다. 빠른 시일 내에 심사를 시작하겠습니다.
              </Alert>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default AppealDetailPage;
